import { GaiaEventType, WebsocketSubscriptionTarget, type GaiaEvent } from '@/api';
import { getWsClient, type WebSocketEventHandler } from '@/services/WebSocketService';
import { WEBSOCKET_DISABLED_KEY } from '@/composables/useReviewWebSocket';
import { useDebounceFn } from '@vueuse/core';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { logger } from '@/utils/logger';

export interface UseReviewProjectWebSocketOptions {
  projectUid: () => string;
  /** An analysis in this project was created/updated/deleted — refetch the list. */
  onAnalysesStale?: () => void;
  /** A question in this project was created/updated/deleted — refetch the Q&A list. */
  onQuestionsStale?: () => void;
  /** The project's report content changed — refetch it. */
  onReportStale?: () => void;
}

const ANALYSIS_EVENT_TYPES = new Set<GaiaEventType>([
  GaiaEventType.REVIEW_PROJECT_ANALYSIS_CREATED,
  GaiaEventType.REVIEW_PROJECT_ANALYSIS_UPDATED,
  GaiaEventType.REVIEW_PROJECT_ANALYSIS_DELETED,
]);

const QUESTION_EVENT_TYPES = new Set<GaiaEventType>([
  GaiaEventType.REVIEW_PROJECT_QUESTION_CREATED,
  GaiaEventType.REVIEW_PROJECT_QUESTION_UPDATED,
  GaiaEventType.REVIEW_PROJECT_QUESTION_DELETED,
]);

/**
 * React to a review project's `review_project_*` events. Their payload is only the
 * **project** uid (a "this changed" signal, no entity data), so the sole reaction is a
 * refetch — debounced per group, as a create arrives back-to-back with an update when
 * settings are saved right away.
 */
export function useReviewProjectWebSocket(options: UseReviewProjectWebSocketOptions): void {
  const unsubscribe = ref<(() => void) | null>(null);

  const notifyAnalysesStale = useDebounceFn(() => options.onAnalysesStale?.(), 300);
  const notifyQuestionsStale = useDebounceFn(() => options.onQuestionsStale?.(), 300);
  const notifyReportStale = useDebounceFn(() => options.onReportStale?.(), 300);

  const handler: WebSocketEventHandler = (event: GaiaEvent) => {
    const data = event.data;
    const uid = typeof data === 'object' ? (data as { uid?: unknown }).uid : undefined;
    if (uid !== options.projectUid()) {
      return;
    }
    if (ANALYSIS_EVENT_TYPES.has(event.gaia_event_type)) {
      void notifyAnalysesStale();
    } else if (QUESTION_EVENT_TYPES.has(event.gaia_event_type)) {
      void notifyQuestionsStale();
    } else if (event.gaia_event_type === GaiaEventType.REVIEW_PROJECT_REPORT_UPDATED) {
      void notifyReportStale();
    }
  };

  onMounted(() => {
    if (localStorage.getItem(WEBSOCKET_DISABLED_KEY) === 'true') {
      logger.warn('[WebSocket] WebSocket is disabled via settings');
      return;
    }
    const projectUid = options.projectUid();
    if (!projectUid) {
      logger.warn('[WebSocket] Missing review project uid for subscription');
      return;
    }

    unsubscribe.value = getWsClient().subscribe(
      WebsocketSubscriptionTarget.REVIEW_PROJECT,
      projectUid,
      handler
    );
  });

  onBeforeUnmount(() => {
    unsubscribe.value?.();
    unsubscribe.value = null;
  });
}
